import {Add16} from "@bphxd/ds-core-react/lib/icons";
import {ISCC_EU, ISCC_PLUS} from "constants/certificateTemplateDetails";

import {COUNTRY_GERMANY} from "constants/countryDetails";
import {DIV_CODE_COPRO, DIV_CODE_SAF} from "constants/divisionDetails";
import {sortBy} from "lodash";
import {
  generateFieldValidationSchema,
  getQuantityKeyByCountryAndDivision,
  getUomKeyByCountryAndDivision,
  shouldSplitsBeShownByCountryAndDivision,
} from "modules/DocManager/content/getDetailedViewConfig";
import CheckboxFlag from "modules/common/BinaryFlag";
import Feature from "modules/common/FeatureFlag/Feature";
import {useFeatureFlag} from "modules/common/FeatureFlag/FeatureFlagContext";
import FEATURE_FLAGS from "modules/common/FeatureFlag/featureFlagConstants";
import PropTypes from "prop-types";
import {useUserSettings} from "providers/userSettings";
import {useMemo} from "react";
import {useFormContext} from "react-hook-form";
import {Button, FormFeedback, FormGroup, Input, Label} from "reactstrap";
import {getRequiredNumberInputPattern, handleNumber} from "utils/numberUtil";
import Datepicker from "./Datepicker/index";
import UnitOfMeasure from "./UnitOfMeasure";
import RecyclingType from "./recyclingOperation";
import WasteStatus from "./wasteStatus";

const GeneralInformation = ({
  countries,
  status,
  rawMaterialTypes = [],
  productTypeItems = [],
  selectedCountry,
  isDisabled,
  divisionCode,
  fields,
  createAdditionalSplit,
  certificationSystem,
}) => {
  const {
    register,
    formState: {errors},
    control,
    watch,
  } = useFormContext();

  const {
    userSettings: {decimalFormat},
  } = useUserSettings();

  const computeProps = (name, options) => {
    const {ref, ...props} = register(name, options);
    return {innerRef: ref, ...props};
  };
  const productTypeItemsSpain = ["Pure Raw material", "Refined Raw Material"];
  const chainOfCustody = ["Mass Balance", "Physical Segregation"];
  const rawMaterialCategory = ["Circular", "Bio-Circular", "Bio", "Renewable"];
  const splitDetails = watch("splitDetails");

  const rawMaterialTypesSorted = sortBy(
    rawMaterialTypes,
    "rawMaterialName",
    "asc",
  );

  const unitOfMeasureKey = getUomKeyByCountryAndDivision(
    selectedCountry,
    divisionCode,
  );

  const watchCertificationSystem = watch("certificationSystem");

  const unitOfMeasureValue =
    watch(unitOfMeasureKey) && watch(unitOfMeasureKey).toUpperCase() === "M3"
      ? "m³"
      : "MT";

  const quantityKey = getQuantityKeyByCountryAndDivision(
    selectedCountry,
    divisionCode,
  );

  const requiredNumberInputPattern = useMemo(
    () => getRequiredNumberInputPattern(decimalFormat),
    [decimalFormat],
  );

  const DOC_MAN_DE_SAF_CHANGES = useFeatureFlag(
    FEATURE_FLAGS.DOC_MAN_DE_SAF_CHANGES,
  );

  const hideSplitsUK = useFeatureFlag(FEATURE_FLAGS.HIDE_SPLITS_UK);

  const shouldRender = useMemo(() => {
    if (
      DOC_MAN_DE_SAF_CHANGES &&
      divisionCode === DIV_CODE_SAF &&
      watchCertificationSystem === ISCC_EU
    ) {
      return true;
    }
    return false;
  }, [DOC_MAN_DE_SAF_CHANGES, divisionCode, watchCertificationSystem]);

  const displaySplits = useMemo(
    () =>
      shouldSplitsBeShownByCountryAndDivision(
        selectedCountry,
        divisionCode,
        hideSplitsUK,
      ),
    [selectedCountry, divisionCode, hideSplitsUK],
  );

  return (
    <div className="flex flex-col gap-5 pb-6">
      <FormGroup>
        <Label for="productType" className="fw-normal">
          Type of product
        </Label>
        <Input
          type="select"
          id="productType"
          data-test="productType"
          {...computeProps("productType", {
            required: "Please enter type of product",
          })}
          invalid={!!errors.productType && !isDisabled}
          maxLength={100}
          disabled={isDisabled}
          placeholder="Please choose from list"
        >
          <option value="">Please choose from list</option>

          {productTypeItems.map((productTypeItem, index) => (
            <option key={index} value={productTypeItem.productName}>
              {productTypeItem.productName}
            </option>
          ))}
        </Input>
        {errors.productType && !isDisabled && (
          <FormFeedback>{errors.productType.message}</FormFeedback>
        )}
      </FormGroup>
      {selectedCountry === COUNTRY_GERMANY &&
        divisionCode === DIV_CODE_SAF &&
        watchCertificationSystem === ISCC_PLUS && (
          <Feature flag={FEATURE_FLAGS.DOC_MAN_DE_SAF_CHANGES}>
            <FormGroup>
              <Label for="rawMaterialCategory" className="fw-normal">
                Raw material category
              </Label>
              <Input
                type="select"
                id="rawMaterialCategory"
                data-test="rawMaterialCategory"
                {...computeProps("rawMaterialCategory")}
                invalid={!!errors.rawMaterialCategory && !isDisabled}
                disabled={isDisabled}
                placeholder="Please choose from list"
              >
                <option value="">Please choose from list</option>
                {rawMaterialCategory.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </Input>
            </FormGroup>
          </Feature>
        )}
      <FormGroup>
        <Label for="rawMaterialType" className="fw-normal">
          Type of raw material
        </Label>

        <Input
          type="select"
          id="rawMaterialType"
          data-test="rawMaterialType"
          {...computeProps("rawMaterialType", {
            required: "Please enter type of raw material",
          })}
          invalid={!!errors.rawMaterialType && !isDisabled}
          maxLength={100}
          disabled={isDisabled}
          placeholder="Please choose from list"
        >
          <option value="">Please choose from list</option>

          {rawMaterialTypesSorted.length > 0 &&
            rawMaterialTypesSorted?.map((p, i) => (
              <option key={i} value={p.rawMaterialName}>
                {p.rawMaterialName}
              </option>
            ))}
        </Input>

        {errors.rawMaterialType && !isDisabled && (
          <FormFeedback>{errors.rawMaterialType.message}</FormFeedback>
        )}
      </FormGroup>
      <FormGroup>
        <Label for="additionalInfo" className="fw-normal">
          Additional information{" "}
          {divisionCode === DIV_CODE_SAF && "(voluntary)"}
        </Label>
        <Input
          type="textarea"
          rows="3"
          id="additionalInfo"
          data-test="additionalInfo"
          {...computeProps("additionalInfo")}
          invalid={!!errors.additionalInfo && !isDisabled}
          maxLength={250}
          disabled={isDisabled}
        />
      </FormGroup>
      {(!splitDetails || splitDetails?.length === 0) && (
        <FormGroup>
          <Label for="rawMaterialOriginCountryName" className="fw-normal">
            Country of origin (of the origin raw material)
          </Label>
          <Input
            type="select"
            id="rawMaterialOriginCountryName"
            data-test="rawMaterialOriginCountryName"
            {...computeProps(
              "rawMaterialOriginCountryName",
              generateFieldValidationSchema({
                country: selectedCountry,
                division: divisionCode,
                certificationSystem,
                field: "rawMaterialOriginCountryName",
              }),
            )}
            invalid={!!errors.rawMaterialOriginCountryName && !isDisabled}
            disabled={isDisabled}
          >
            {countries.map((country) => (
              <option key={country.code} value={country.name}>
                {country.name}
              </option>
            ))}
          </Input>
          {errors.rawMaterialOriginCountryName && !isDisabled && (
            <FormFeedback>
              {errors.rawMaterialOriginCountryName.message}
            </FormFeedback>
          )}
        </FormGroup>
      )}
      {!isDisabled && displaySplits && (
        <Feature flag={FEATURE_FLAGS.DOC_MANAGER_SPLITS_PERCENTAGE}>
          <div>
            <Button
              size="xs"
              onClick={createAdditionalSplit}
              color="standard-primary"
              className="primary-btn rounded-0 disabled:filter disabled:brightness-75 disabled:!opacity-50"
              disabled={fields.length > 0}
            >
              <Add16 className="btn-icon-prefix" />
              Create split
            </Button>
          </div>
        </Feature>
      )}
      <FormGroup>
        <Label for={quantityKey} className="fw-normal">
          Quantity {unitOfMeasureValue}
        </Label>
        <Input
          type="text"
          id={quantityKey}
          data-test={quantityKey}
          {...computeProps(quantityKey, {
            required: "Please enter quantity",
            ...requiredNumberInputPattern,
          })}
          invalid={!!errors[quantityKey] && !isDisabled}
          disabled={isDisabled}
          maxLength={20}
          onKeyDown={(e) => handleNumber(e)}
        />

        {errors[quantityKey] && !isDisabled && (
          <FormFeedback>{errors[quantityKey].message}</FormFeedback>
        )}
      </FormGroup>
      <FormGroup>
        <Label for={unitOfMeasureKey} className="fw-normal">
          Unit of measure
        </Label>
        <UnitOfMeasure
          id={unitOfMeasureKey}
          name={unitOfMeasureKey}
          control={control}
          error={errors[unitOfMeasureKey]}
          disabled={isDisabled}
          rules={{
            validate: (value) =>
              value === null || value === ""
                ? "Please select unit of measure"
                : true,
          }}
        />
      </FormGroup>
      {selectedCountry === COUNTRY_GERMANY &&
        divisionCode === DIV_CODE_SAF &&
        watchCertificationSystem === ISCC_PLUS && (
          <Feature flag={FEATURE_FLAGS.DOC_MAN_DE_SAF_CHANGES}>
            <FormGroup>
              <Label for="deliveryQuantity" className="fw-normal">
                Total quantity of delivery (optional)
              </Label>
              <Input
                type="text"
                id="deliveryQuantity"
                data-test="deliveryQuantity"
                {...computeProps("deliveryQuantity", {
                  ...requiredNumberInputPattern,
                })}
                invalid={!!errors.deliveryQuantity && !isDisabled}
                disabled={isDisabled}
                maxLength={20}
                onKeyDown={(e) => handleNumber(e)}
              />
              {errors.deliveryQuantity && !isDisabled && (
                <FormFeedback>{errors.deliveryQuantity.message}</FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="percentageCertifiedMaterial" className="fw-normal">
                Percentage of certified material (% of the toal quantity of
                delivery)
              </Label>
              <Input
                type="text"
                id="percentageCertifiedMaterial"
                data-test="percentageCertifiedMaterial"
                {...computeProps("percentageCertifiedMaterial", {
                  ...requiredNumberInputPattern,
                })}
                invalid={!!errors.percentageCertifiedMaterial && !isDisabled}
                disabled={isDisabled}
                maxLength={20}
                onKeyDown={(e) => handleNumber(e)}
                placeholder="NA"
              />
              {errors.percentageCertifiedMaterial && !isDisabled && (
                <FormFeedback>
                  {errors.percentageCertifiedMaterial.message}
                </FormFeedback>
              )}
            </FormGroup>
            <FormGroup>
              <Label for="recyclingOperationType" className="fw-normal">
                Type of recycling operations (circular)
              </Label>
              <RecyclingType
                id="recyclingOperationType"
                name="recyclingOperationType"
                control={control}
                error={errors.recyclingOperationType}
                disabled={isDisabled}
              />
            </FormGroup>
            <FormGroup>
              <Label for="wasteStatus" className="fw-normal">
                Waste status (circular)
              </Label>
              <WasteStatus
                id="wasteStatus"
                name="wasteStatus"
                control={control}
                error={errors.wasteStatus}
                disabled={isDisabled}
              />
            </FormGroup>
          </Feature>
        )}
      {divisionCode !== DIV_CODE_COPRO && (
        <FormGroup>
          <Label for="productEnergyContent" className="fw-normal mb-4">
            Energy content (MJ)
          </Label>
          <Input
            type="text"
            id="productEnergyContent"
            data-test="productEnergyContent"
            {...computeProps(
              "productEnergyContent",
              generateFieldValidationSchema({
                country: selectedCountry,
                division: divisionCode,
                certificationSystem,
                field: "productEnergyContent",
                decimalFormat,
              }),
            )}
            invalid={!!errors.productEnergyContent && !isDisabled}
            disabled={isDisabled}
            onKeyDown={(e) => handleNumber(e)}
            maxLength={20}
          />
          {errors.productEnergyContent && !isDisabled && (
            <FormFeedback className="mt-2">
              {errors.productEnergyContent.message}
            </FormFeedback>
          )}
        </FormGroup>
      )}
      <FormGroup>
        <Label for="isccCompliantFlag" className="fw-normal">
          ISCC compliant material{" "}
          {divisionCode === DIV_CODE_SAF && "(voluntary)"}
        </Label>
        <CheckboxFlag
          id="isccCompliantFlag"
          name="isccCompliantFlag"
          control={control}
          error={errors.isccCompliantFlag}
          rules={generateFieldValidationSchema({
            country: selectedCountry,
            division: divisionCode,
            certificationSystem,
            field: "isccCompliantFlag",
          })}
          disabled={isDisabled}
        />
      </FormGroup>
      <FormGroup>
        <Label for="euRedCompliantFlag" className="fw-normal">
          EU RED compliant material
        </Label>
        <CheckboxFlag
          id="euRedCompliantFlag"
          name="euRedCompliantFlag"
          control={control}
          error={errors.euRedCompliantFlag}
          rules={generateFieldValidationSchema({
            country: selectedCountry,
            division: divisionCode,
            certificationSystem,
            field: "euRedCompliantFlag",
          })}
          disabled={isDisabled}
        />
      </FormGroup>
      <FormGroup>
        <Label for="chainOfCustodyOption" className="fw-normal">
          Chain of custody option (voluntary)
        </Label>
        <Input
          type="select"
          id="chainOfCustodyOption"
          data-test="chainOfCustodyOption"
          {...computeProps("chainOfCustodyOption")}
          invalid={!!errors.chainOfCustodyOption && !isDisabled}
          disabled={isDisabled}
        >
          {chainOfCustody.map((option) => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </Input>
      </FormGroup>
      {selectedCountry === COUNTRY_GERMANY &&
        divisionCode === DIV_CODE_SAF &&
        watchCertificationSystem === ISCC_PLUS && (
          <Feature flag={FEATURE_FLAGS.DOC_MAN_DE_SAF_CHANGES}>
            <FormGroup>
              <Label for="mbOption" className="fw-normal">
                Mass balance option
              </Label>
              <Input
                type="text"
                id="mbOption"
                data-test="mbOption"
                {...computeProps("mbOption")}
                invalid={!!errors.mbOption && !isDisabled}
                maxLength={50}
                disabled={isDisabled}
              />
            </FormGroup>
            <FormGroup>
              <Label for="multiSiteCreditTransferFlag" className="fw-normal">
                Multi-site credit transfer
              </Label>
              <CheckboxFlag
                id="multiSiteCreditTransferFlag"
                name="multiSiteCreditTransferFlag"
                control={control}
                error={errors.multiSiteCreditTransferFlag}
                disabled={isDisabled}
              />
            </FormGroup>
          </Feature>
        )}
      {shouldRender && (
        <>
          <FormGroup>
            <Label for="countryOfBiofuelProduction" className="fw-normal">
              Country of biofuel production
            </Label>
            <Input
              type="text"
              id="countryOfBiofuelProduction"
              data-test="countryOfBiofuelProduction"
              {...computeProps("countryOfBiofuelProduction")}
              invalid={!!errors.mbOption && !isDisabled}
              maxLength={100}
              disabled={isDisabled}
            />
          </FormGroup>
          <FormGroup>
            <Label for="producerInstallationDate" className="fw-normal mb-4">
              Start date of biofuel production
            </Label>
            <Datepicker
              id="producerInstallationDate"
              name="producerInstallationDate"
              control={control}
              error={errors.producerInstallationDate}
              disabled={isDisabled}
            />
          </FormGroup>
          <FormGroup>
            <Label for="userInstallationDate" className="fw-normal mb-4">
              if applicable, start date of bioliquid/biomass fuel use
            </Label>
            <Datepicker
              id="userInstallationDate"
              name="userInstallationDate"
              control={control}
              error={errors.userInstallationDate}
              disabled={isDisabled}
            />
          </FormGroup>
        </>
      )}
    </div>
  );
};
GeneralInformation.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.object).isRequired,
  status: PropTypes.string.isRequired,
  selectedCountry: PropTypes.string,
  rawMaterialTypes: PropTypes.arrayOf(PropTypes.object),
  productTypeItems: PropTypes.arrayOf(PropTypes.object),
  isDisabled: PropTypes.bool,
  divisionCode: PropTypes.string,
  certificationSystem: PropTypes.string,
  fields: PropTypes.array,
  createAdditionalSplit: PropTypes.func,
};
export default GeneralInformation;
